import { useEffect, useState } from "react";

/**
 * Custom hook to automatically focus on the active tab when a component with tabs
 * is initially loaded or when the tab changes.
 * 
 * This improves accessibility and user experience by:
 * - Complying with WCAG 2.4.3 (Focus Order) - Sets logical focus on page load
 * - Following WCAG 2.4.7 (Focus Visible) - Ensures focus is visible and predictable
 * - Providing keyboard users with immediate context about which tab is active
 * - Dispatching mousedown events to trigger MUI's TouchRipple animation
 * - Providing visual feedback consistent with user click interactions
 * - Using requestAnimationFrame to wait for elements to be fully rendered (no arbitrary delays)
 * 
 * @param {Object} options - Configuration options
 * @param {number|string} options.activeTabIndex - The currently active tab index or identifier
 * @param {string} options.tabIdPrefix - Prefix for tab element IDs (e.g., 'biller-profile-tab')
 * @param {boolean} options.enabled - Whether the hook is enabled (default: true)
 * @param {boolean} options.focusOnChange - Whether to focus when tab changes (default: false, only on initial load)
 * @param {Function} options.getTabId - Custom function to generate tab ID (optional, overrides tabIdPrefix)
 * @param {boolean} options.triggerRipple - Whether to trigger MUI's TouchRipple animation via mousedown/mouseup events (default: true)
 * 
 * @returns {Object} - Returns isInitialLoad state for conditional rendering if needed
 * 
 * @example
 * // Basic usage - focus on initial load only
 * const [tabIndex, setTabIndex] = useState(0);
 * 
 * useTabFocus({
 *   activeTabIndex: tabIndex,
 *   tabIdPrefix: 'my-tab'
 * });
 * 
 * // In your Tabs component
 * <Tab id="my-tab-0" label="Tab 1" />
 * <Tab id="my-tab-1" label="Tab 2" />
 * 
 * @example
 * // Focus on every tab change
 * useTabFocus({
 *   activeTabIndex: tabIndex,
 *   tabIdPrefix: 'my-tab',
 *   focusOnChange: true
 * });
 * 
 * @example
 * // Custom tab ID generation
 * useTabFocus({
 *   activeTabIndex: tabIndex,
 *   getTabId: (index) => `custom-tab-${index}-button`
 * });
 * 
 * @example
 * // Conditional enabling
 * useTabFocus({
 *   activeTabIndex: tabIndex,
 *   tabIdPrefix: 'my-tab',
 *   enabled: !isLoading
 * });
 * 
 * @example
 * // Disable ripple animation (only focus, no visual ripple effect)
 * useTabFocus({
 *   activeTabIndex: tabIndex,
 *   tabIdPrefix: 'my-tab',
 *   triggerRipple: false
 * });
 */
const useTabFocus = ({
  activeTabIndex,
  tabIdPrefix,
  enabled = true,
  focusOnChange = false,
  getTabId = null,
  triggerRipple = true
} = {}) => {
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  useEffect(() => {
    if (!enabled) return;

    // Determine if we should focus based on configuration
    const shouldFocus = isInitialLoad || focusOnChange;

    if (shouldFocus && activeTabIndex !== undefined && activeTabIndex !== null) {
      // Generate tab ID using custom function or default pattern
      const tabId = getTabId 
        ? getTabId(activeTabIndex)
        : `${tabIdPrefix}-${activeTabIndex}`;
      
      /**
       * Waits for the tab element to be fully rendered and ready
       * Uses requestAnimationFrame to wait for next paint cycle
       */
      const waitForElementReady = (callback) => {
        const activeTab = document.getElementById(tabId);
        
        if (!activeTab) {
          // Element not found yet, try again after a small delay
          if (process.env.NODE_ENV === 'development') {
            console.warn(`useTabFocus: Tab element with ID "${tabId}" not found, retrying...`);
          }
          const retryTimer = setTimeout(() => waitForElementReady(callback), 50);
          return () => clearTimeout(retryTimer);
        }
        
        // Element exists, wait for next browser paint to ensure it's fully rendered
        // requestAnimationFrame waits for the next repaint
        requestAnimationFrame(() => {
          // Double RAF ensures layout and paint are complete
          requestAnimationFrame(() => {
            // Additional check: ensure element has dimensions (is rendered)
            const rect = activeTab.getBoundingClientRect();
            if (rect.width > 0 && rect.height > 0) {
              // Element is fully rendered and visible
              callback(activeTab);
            } else {
              // Element not yet visible, wait a bit more
              setTimeout(() => waitForElementReady(callback), 10);
            }
          });
        });
      };
      
      // Execute focus logic when element is ready
      const cleanup = waitForElementReady((activeTab) => {
        // Blur current element first to ensure clean focus transition
        if (document.activeElement && document.activeElement !== activeTab) {
          document.activeElement.blur();
        }
        
        // Focus the tab using native focus method
        activeTab.focus();
        
        if (triggerRipple) {
          // MUI's TouchRipple component listens for mousedown/mouseup events
          // Use requestAnimationFrame to ensure focus is applied before triggering ripple
          requestAnimationFrame(() => {
            const rect = activeTab.getBoundingClientRect();
            const centerX = rect.left + rect.width / 2;
            const centerY = rect.top + rect.height / 2;
            
            // Dispatch mousedown to start ripple
            const mouseDownEvent = new MouseEvent('mousedown', {
              view: window,
              bubbles: true,
              cancelable: true,
              clientX: centerX,
              clientY: centerY,
              button: 0,
              buttons: 1
            });
            activeTab.dispatchEvent(mouseDownEvent);
            
            // Dispatch mouseup immediately after to complete the ripple cycle
            // This prevents the ripple from staying visible indefinitely
            requestAnimationFrame(() => {
              const mouseUpEvent = new MouseEvent('mouseup', {
                view: window,
                bubbles: true,
                cancelable: true,
                clientX: centerX,
                clientY: centerY,
                button: 0,
                buttons: 0
              });
              activeTab.dispatchEvent(mouseUpEvent);
            });
          });
        }
        
        // Mark initial load as complete after first successful focus
        if (isInitialLoad) {
          setIsInitialLoad(false);
        }
      });

      return cleanup;
    }
  }, [activeTabIndex, tabIdPrefix, enabled, focusOnChange, isInitialLoad, getTabId, triggerRipple]);

  // Return state that can be used by component if needed
  return { isInitialLoad };
};

export default useTabFocus;

